var _ExpandMore;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { string, object, bool, oneOf, func, node } from 'prop-types';
import { ExpandMore } from 'mosaic-react-icons';
import { styled } from '@mui/material/styles';
import SovosButton from '../sovos-button';
import SovosIconMenu from '../sovos-icon-menu';
import splitButtonStyles from './splitButtonStyles';
const Root = styled('div')({
  alignItems: 'center',
  display: 'flex'
});
const DefaultButton = styled(SovosButton)(_ref => {
  let {
    disabled,
    theme: {
      spacing
    },
    variant
  } = _ref;
  const modifier = disabled ? 'disabled' : 'default';
  const borderRadius = "".concat(spacing(1), " 0  0 ").concat(spacing(1));
  const borderInlineEnd = '0 !important';
  const {
    border,
    boxShadow,
    hover,
    padding
  } = splitButtonStyles.get(modifier)[variant];
  return {
    border,
    borderRadius,
    borderInlineEnd,
    padding,
    '&:hover': {
      backgroundColor: hover === null || hover === void 0 ? void 0 : hover.backgroundColor,
      border: hover === null || hover === void 0 ? void 0 : hover.border,
      borderInlineEnd,
      boxShadow,
      color: hover === null || hover === void 0 ? void 0 : hover.color
    },
    '&:hover:focus': {
      backgroundColor: hover === null || hover === void 0 ? void 0 : hover.backgroundColor,
      border: hover === null || hover === void 0 ? void 0 : hover.border,
      color: hover === null || hover === void 0 ? void 0 : hover.color
    }
  };
});
const IconMenu = styled(SovosIconMenu)(_ref2 => {
  let {
    IconButtonProps,
    theme: {
      spacing
    },
    variant
  } = _ref2;
  const {
    disabled
  } = IconButtonProps;
  const modifier = disabled ? 'disabled' : 'default';
  const borderRadius = "0 ".concat(spacing(1), " ").concat(spacing(1), " 0");
  const height = spacing(4);
  const outline = {
    '&:focus-visible::after': {
      outlineOffset: 4
    }
  };
  const {
    backgroundColor,
    border,
    boxShadow,
    color,
    divider,
    hover
  } = splitButtonStyles.get(modifier)[variant];
  return {
    backgroundColor,
    border,
    borderRadius,
    boxShadow,
    height,
    '&.sovosIconButton': {
      border,
      borderInlineStart: divider,
      boxShadow,
      color,
      '&:hover': {
        border: hover === null || hover === void 0 ? void 0 : hover.border,
        borderInlineStart: "".concat(hover === null || hover === void 0 ? void 0 : hover.divider, " !important"),
        backgroundColor: hover === null || hover === void 0 ? void 0 : hover.backgroundColor,
        color: hover === null || hover === void 0 ? void 0 : hover.color,
        '&:focus': {
          backgroundColor: hover === null || hover === void 0 ? void 0 : hover.backgroundColor,
          border,
          color: hover === null || hover === void 0 ? void 0 : hover.color
        }
      }
    },
    ...outline
  };
});
const SovosSplitButton = /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    children,
    classes,
    className,
    'data-testid': dataTestId,
    disabled = false,
    menuItems,
    menuTooltipText,
    onClick,
    tooltipText,
    variant = 'contained',
    ...rest
  } = _ref3;
  return /*#__PURE__*/React.createElement(Root, _extends({
    className: clsx('sovosSplitIcon', className),
    "data-testid": dataTestId
  }, rest), /*#__PURE__*/React.createElement(DefaultButton, {
    disabled: disabled,
    disableRipple: true,
    onClick: onClick,
    tooltipText: tooltipText,
    variant: variant
  }, children), /*#__PURE__*/React.createElement(IconMenu, {
    IconButtonProps: {
      disabled
    },
    iconElement: _ExpandMore || (_ExpandMore = /*#__PURE__*/React.createElement(ExpandMore, null)),
    ref: ref,
    tooltipText: menuTooltipText,
    variant: variant
  }, menuItems));
});
SovosSplitButton.propTypes = {
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Default action component
   */
  children: node,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * If `true`, the button is disabled
   */
  disabled: bool,
  /**
   * The content of the menu. Must be an array of `SovosMenuItem` or `SovosDivider`
   */
  menuItems: node.isRequired,
  /**
   * Menu Tooltip text
   */
  menuTooltipText: string,
  /**
   * Callback fired when the button is clicked.
   */
  onClick: func,
  /**
   * Tooltip text
   */
  tooltipText: string,
  /**
   * Button variant
   */
  variant: oneOf(['contained', 'outlined', 'text'])
};
export default SovosSplitButton;